import axios from 'axios'
import { ChatSession, ChatMessage, ChatSuggestion } from '@/store/slices/ragChatSlice'

const RAG_API_BASE_URL = import.meta.env.VITE_RAG_API_URL || '/api/rag'

class RAGChatService {
  private apiClient = axios.create({
    baseURL: RAG_API_BASE_URL,
    timeout: 60000,
    headers: {
      'Content-Type': 'application/json'
    }
  })

  constructor() {
    // 添加請求攔截器，自動添加認證 token
    this.apiClient.interceptors.request.use((config) => {
      const token = localStorage.getItem('auth_token')
      if (token) {
        config.headers.Authorization = `Bearer ${token}`
      }
      return config
    })

    // 添加回應攔截器處理錯誤
    this.apiClient.interceptors.response.use(
      (response) => response,
      (error) => {
        console.error('RAG Chat API Error:', error)
        throw error
      }
    )
  }

  /**
   * 發送聊天訊息到 RAG 系統
   */
  async sendMessage(payload: {
    query: string
    sessionId?: string
    context?: ChatSession['context']
    mode?: 'smart' | 'lightrag' | 'context-aware'
  }): Promise<{
    answer: string
    sources?: Array<{
      content: string
      score?: number
      metadata?: Record<string, any>
    }>
    processing_time?: number
    mode?: string
  }> {
    const { query, sessionId, context, mode = 'smart' } = payload

    let endpoint = '/query'
    let requestData: any = {
      query,
      mode: 'hybrid',
      top_k: 10,
      include_metadata: true
    }

    // 根據查詢模式選擇不同的 API 端點
    switch (mode) {
      case 'context-aware':
        endpoint = '/context-aware/query'
        requestData = {
          query,
          context,
          settings: {
            include_multimodal: true,
            max_results: 10
          }
        }
        break
        
      case 'lightrag':
        endpoint = '/query'
        requestData = {
          query,
          mode: 'hybrid',
          top_k: 10,
          include_metadata: true
        }
        break
        
      case 'smart':
      default:
        // 智能模式：根據查詢內容和上下文自動選擇最佳方法
        if (context?.currentEntity || (context?.userContext.recentActions.length ?? 0) > 0) {
          endpoint = '/context-aware/query'
          requestData = {
            query,
            context,
            settings: {
              max_results: 8,
              include_metadata: true
            }
          }
        } else {
          endpoint = '/query'
          requestData = {
            query,
            mode: 'hybrid',
            top_k: 8,
            include_metadata: true
          }
        }
        break
    }

    // 如果有會話 ID，添加到請求中
    if (sessionId) {
      requestData.sessionId = sessionId
    }

    console.log('RAG Service: Making request to', endpoint, 'with data:', requestData);
    const response = await this.apiClient.post(endpoint, requestData)
    console.log('RAG Service: Received response:', response.data);
    
    // 後端響應格式：{ success: true, data: result, responseTime, timestamp }
    if (!response.data.success) {
      console.error('RAG Service: Request failed:', response.data.error);
      throw new Error(response.data.error || '搜索請求失敗')
    }

    const ragResult = response.data.data
    
    // 檢查 RAG 結果格式
    if (!ragResult) {
      return {
        answer: "抱歉，我在資料庫中找不到相關的資訊來回答您的問題。請嘗試重新描述您的問題或聯繫系統管理員。",
        sources: [],
        processing_time: response.data.responseTime || 0,
        mode
      }
    }

    // RAG 服務直接返回答案格式
    if (ragResult.answer) {
      return {
        answer: ragResult.answer,
        sources: ragResult.sources || [],
        processing_time: response.data.responseTime || 0,
        mode
      }
    }

    // 如果返回的是搜索結果格式，需要轉換成答案
    const sources = ragResult.sources || []
    
    if (sources.length === 0) {
      return {
        answer: "抱歉，我在資料庫中找不到相關的資訊來回答您的問題。請嘗試重新描述您的問題或聯繫系統管理員。",
        sources: [],
        processing_time: response.data.responseTime || 0,
        mode
      }
    }

    // 根據搜尋結果生成回答
    const topSources = sources.slice(0, 5)
    let answer = "根據您的查詢，我找到了以下相關資訊：\n\n"
    
    topSources.forEach((source: any, index: number) => {
      const content = source.content || source.text || ''
      const title = source.metadata?.title || source.title || `資料 ${index + 1}`
      answer += `${index + 1}. **${title}**\n`
      answer += `${content.substring(0, 200)}${content.length > 200 ? '...' : ''}\n\n`
    })

    const processing_time = response.data.responseTime || 0
    answer += `\n---\n找到 ${sources.length} 條相關結果，處理時間：${(processing_time / 1000).toFixed(2)}秒`

    return {
      answer,
      sources: topSources,
      processing_time,
      mode
    }
  }

  /**
   * 建立新的聊天會話（本地實現）
   */
  async createSession(payload: {
    name?: string
    projectId?: string
    context?: ChatSession['context']
  }): Promise<ChatSession> {
    console.log('RAGChatService: Creating session with payload:', payload)
    
    // 由於 RAG 服務沒有會話管理，我們在前端本地創建
    const session: ChatSession = {
      id: `session-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      name: payload.name || `AI 助手 ${new Date().toLocaleString()}`,
      userId: 'current-user', // 這應該從認證狀態獲取
      projectId: payload.projectId,
      context: payload.context,
      createdAt: new Date().toISOString(),
      updatedAt: new Date().toISOString()
    }

    console.log('RAGChatService: Created session object:', session)

    // 保存到 localStorage
    const sessions = await this.getSessions()
    sessions.unshift(session)
    localStorage.setItem('ragChatSessions', JSON.stringify(sessions))
    
    console.log('RAGChatService: Saved session to localStorage, total sessions:', sessions.length)

    return session
  }

  /**
   * 獲取聊天會話列表（本地實現）
   */
  async getSessions(): Promise<ChatSession[]> {
    console.log('RAGChatService: Getting sessions from localStorage...')
    
    // 從 localStorage 獲取會話列表
    const sessionsData = localStorage.getItem('ragChatSessions')
    const sessions = sessionsData ? JSON.parse(sessionsData) : []
    
    console.log('RAGChatService: Raw sessions data:', sessionsData)
    console.log('RAGChatService: Parsed sessions:', sessions)
    
    // 確保所有日期都是字符串格式
    const processedSessions = sessions.map((session: any) => ({
      ...session,
      createdAt: typeof session.createdAt === 'string' ? session.createdAt : new Date(session.createdAt).toISOString(),
      updatedAt: typeof session.updatedAt === 'string' ? session.updatedAt : new Date(session.updatedAt).toISOString(),
      context: session.context ? {
        ...session.context,
        userContext: session.context.userContext ? {
          ...session.context.userContext,
          recentActions: session.context.userContext.recentActions?.map((action: any) => ({
            ...action,
            timestamp: typeof action.timestamp === 'string' ? action.timestamp : new Date(action.timestamp).toISOString()
          })) || []
        } : session.context.userContext
      } : session.context
    }))
    
    console.log('RAGChatService: Processed sessions:', processedSessions)
    return processedSessions
  }

  /**
   * 獲取聊天會話的訊息列表（本地實現）
   */
  async getMessages(sessionId: string): Promise<ChatMessage[]> {
    // 從 localStorage 獲取訊息
    const messagesData = localStorage.getItem(`ragChatMessages_${sessionId}`)
    const messages = messagesData ? JSON.parse(messagesData) : []
    
    // 確保 timestamp 為字符串格式
    return messages.map((msg: any) => ({
      ...msg,
      timestamp: typeof msg.timestamp === 'string' ? msg.timestamp : new Date(msg.timestamp).toISOString()
    }))
  }

  /**
   * 刪除聊天會話（本地實現）
   */
  async deleteSession(sessionId: string): Promise<void> {
    // 從 localStorage 刪除會話和相關訊息
    const sessions = await this.getSessions()
    const filteredSessions = sessions.filter(s => s.id !== sessionId)
    localStorage.setItem('ragChatSessions', JSON.stringify(filteredSessions))
    localStorage.removeItem(`ragChatMessages_${sessionId}`)
  }

  /**
   * 保存聊天訊息到本地存儲
   */
  async saveMessages(sessionId: string, messages: ChatMessage[]): Promise<void> {
    localStorage.setItem(`ragChatMessages_${sessionId}`, JSON.stringify(messages))
  }

  /**
   * 生成智能查詢建議
   */
  async generateSuggestions(context?: ChatSession['context']): Promise<ChatSuggestion[]> {
    const suggestions: ChatSuggestion[] = []

    // 基於上下文生成建議
    if (context?.currentEntity) {
      const { type } = context.currentEntity
      
      switch (type) {
        case 'project':
          suggestions.push(
            { id: '1', text: `這個專案的進度如何？`, category: 'context', icon: '📊' },
            { id: '2', text: `有哪些重要議題需要處理？`, category: 'context', icon: '🎯' },
            { id: '3', text: `最近的活動記錄是什麼？`, category: 'context', icon: '📋' }
          )
          break
          
        case 'issue':
          suggestions.push(
            { id: '4', text: `這個議題的詳細信息`, category: 'context', icon: '🔍' },
            { id: '5', text: `相關的留言和更新`, category: 'context', icon: '💬' },
            { id: '6', text: `類似的議題有哪些？`, category: 'context', icon: '🔗' }
          )
          break
          
        case 'worklog':
          suggestions.push(
            { id: '7', text: `這個工作日誌包含什麼內容？`, category: 'context', icon: '📝' },
            { id: '8', text: `相關的議題和任務`, category: 'context', icon: '🔗' },
            { id: '9', text: `工作進度統計`, category: 'context', icon: '📈' }
          )
          break
      }
    }

    // 添加常用查詢建議
    const commonSuggestions: ChatSuggestion[] = [
      { id: 'c1', text: '最近有哪些議題更新？', category: 'common', icon: '🆕' },
      { id: 'c2', text: '我負責的任務有哪些？', category: 'common', icon: '👤' },
      { id: 'c3', text: '專案整體進度如何？', category: 'common', icon: '📊' },
      { id: 'c4', text: '需要關注的高優先級議題', category: 'common', icon: '🚨' },
      { id: 'c5', text: '最近上傳的文件有哪些？', category: 'common', icon: '📁' }
    ]

    // 基於最近活動生成建議
    if (context?.userContext.recentActions.length) {
      const recentActions = context.userContext.recentActions.slice(0, 3)
      recentActions.forEach((action, index) => {
        suggestions.push({
          id: `r${index}`,
          text: `關於 ${action.entityType} 的最新狀態`,
          category: 'recent',
          icon: '⏰'
        })
      })
    }

    // 合併所有建議並限制數量
    return [...suggestions, ...commonSuggestions].slice(0, 8)
  }

  /**
   * 獲取 RAG 系統健康狀態
   */
  async getHealthStatus(): Promise<{
    status: 'healthy' | 'degraded' | 'unhealthy'
    services: Record<string, boolean>
  }> {
    try {
      const response = await this.apiClient.get('/health')
      if (!response.data.success) {
        throw new Error('Health check failed')
      }
      
      const healthData = response.data.data
      
      return {
        status: healthData.status === 'healthy' ? 'healthy' : 'degraded',
        services: {
          rag_service: healthData.status === 'healthy',
          lightrag: healthData.components?.rag_services === 'healthy',
          rag_anything: healthData.components?.rag_services === 'healthy',
          database: healthData.components?.database === 'healthy'
        }
      }
    } catch (error) {
      return {
        status: 'unhealthy',
        services: {
          rag_service: false,
          lightrag: false,
          rag_anything: false,
          database: false
        }
      }
    }
  }

  /**
   * 獲取知識圖譜統計
   */
  async getKnowledgeGraphStats(): Promise<{
    entities: number
    relations: number
    documents: number
  }> {
    try {
      const response = await this.apiClient.get('/kg/stats')
      if (!response.data.success) {
        throw new Error('Failed to get stats')
      }
      return response.data.data
    } catch (error) {
      console.error('Failed to get knowledge graph stats:', error)
      return {
        entities: 0,
        relations: 0,
        documents: 0
      }
    }
  }
}

// 建立單例服務實例
export const ragChatService = new RAGChatService()
export default ragChatService
